// Keeps the Notiflix overlays above any open <dialog>.
//
// Every modal in the app is a native <dialog> opened with showModal(), which
// promotes it into the browser's top layer. The top layer paints over the
// whole normal document flow, so a z-index on something attached to <body> -
// which is where Notiflix puts its containers - cannot reach it. 99999 does
// nothing; the containers are simply not in the layer being painted last.
//
// So the containers move into the top layer too. This module owns one host
// <dialog> and hands it out to notify.js. Two properties of the top layer make
// a single host enough:
//
//   - it stacks by promotion order, last promoted on top, ignoring z-index
//   - closing and reopening an element moves it to the top of that order
//
// so the host wins over every dialog, including ones added later, as long as
// it is re-promoted immediately before Notiflix paints. That is what reveal()
// is for. The alternative - moving the containers into whichever dialog
// happens to be open - needs bookkeeping at every showModal() and close() call
// site, and there are more of those than the ticket lists (kundeDetail.js and
// passkeys.js open dialogs too).
//
// A modal <dialog> specifically, not a popover and not dialog.show(): both of
// those were measured against an open modal dialog and lost. Only showModal()
// promotes into the top layer above another modal dialog. That has two costs,
// both paid below: showModal() moves focus, and a modal host would trap
// Escape and swallow clicks meant for the page.

const HOST_ID = 'notiflix-top-layer';

function createHost() {
  const host = document.createElement('dialog');
  host.id = HOST_ID;

  // The host is scenery, not a dialog anyone interacts with: Escape belongs to
  // the real modal underneath, and to Notiflix' own Confirm, which closes
  // itself. Without this the first Escape would only close the host.
  host.addEventListener('cancel', (event) => event.preventDefault());

  document.body.appendChild(host);
  return host;
}

/**
 * The element Notiflix' containers live in. Created on first use, so nothing
 * is added to the DOM on a page that never shows a notification.
 *
 * @returns {HTMLElement} the host dialog
 */
export function topLayerHost() {
  return document.getElementById(HOST_ID) || createHost();
}

// Notiflix deletes its containers once a toast times out or a dialog is
// answered, so an empty host means nothing is on screen. Closing it then is
// what keeps the host from sitting in the top layer as an invisible modal,
// eating clicks and holding focus for the rest of the page's life.
function closeWhenEmpty(host) {
  if (host.childElementCount === 0 && host.open) host.close();
}

/**
 * Promotes the host above every dialog opened so far and moves the given
 * containers into it.
 *
 * Call this immediately after Notiflix paints. Re-promoting on every call is
 * the point: a dialog opened since the last toast would otherwise sit above
 * the host.
 *
 * @param {...(HTMLElement|null)} nodes  containers to adopt, if any exist yet
 */
export function reveal(...nodes) {
  const host = topLayerHost();

  let adopted = false;
  for (const node of nodes) {
    if (node && node.parentNode !== host) {
      host.appendChild(node);
      adopted = true;
    }
  }

  // Nothing of Notiflix' is on screen, so there is nothing to raise and no
  // reason to hold the top layer.
  if (!adopted && host.childElementCount === 0) {
    closeWhenEmpty(host);
    return;
  }

  try {
    if (host.open) host.close();
    host.showModal();
  } catch {
    // A failed promotion costs visibility over a modal. Throwing here would
    // take down the save or delete that the toast was only reporting on.
    return;
  }

  // showModal() moves focus to the host and, because the host is modal, holds
  // it there: focus cannot be handed back to the field underneath while the
  // host is open. That was measured, not assumed - restoring it on the next
  // frame, after a timeout, and with the host inert all leave focus on the
  // host, which is what the spec says should happen.
  //
  // It costs nothing in practice, because the browser returns focus to
  // whatever had it when the host closes, and the host closes as soon as
  // Notiflix removes the last container - about 3s for a toast. So the field
  // a user was typing in has focus again by the time the toast fades. The one
  // thing this does mean: typing is interrupted while a toast is up, which is
  // the same tradeoff Notiflix' own Confirm makes, and the alternative is a
  // toast nobody can see.

  // Notiflix removes each container when its toast times out or its dialog is
  // answered. Nothing notifies us, so the host is polled while it holds
  // something and closed as soon as it is empty. Cheap: one check a second,
  // only while an overlay is actually up.
  watch(host);
}

let watcher = null;

function watch(host) {
  if (watcher) return;
  watcher = window.setInterval(() => {
    if (host.childElementCount === 0) {
      closeWhenEmpty(host);
      window.clearInterval(watcher);
      watcher = null;
    }
  }, 500);
}
